import React, { Component } from 'react';
import { Check, MousePointerClick } from 'lucide-react';
import { GeckoAlert, GeckoFields, ProfileList, Spinner, GeckoBtn } from '../../../../../components';
import ActionBar from './ActionBar';

export default class ScopeSelector extends Component {
    static defaultProps = {
        scopes: [],
        onClick: () => {},
    }

    state = {
        filterResults: null,
    }

    /** 
     * A scope has been clicked on or off, pass its
     * key to the parent method.
     */
    check(key) {
        this.props.onClick(key);
    }

    /**
     * User has entered a search term, update the 
     * filtered results
     */
    filter(term) {
        this.setState({
            filterResults: filter(term, this.props.scopes),
        })
    }

    renderScopesAndGroups(scopes) {
        let groups = groupBy(scopes, 'group');

        const groupElements = Object.keys(groups.items).map((groupKey, key) => {
            return (
                <ScopeGroup key={key} name={groups.items[groupKey].name}>
                    {this.renderScopes(groups.items[groupKey].items)}
                </ScopeGroup>
            );
        });

        return (
            <>
                {this.renderScopes(groups.ungrouped)}
                {groupElements}
            </>
        );
    }

    renderScopes(scopes) {
        return scopes.map((scope, key) => {
            const checked = (this.props.selectedScopes.includes(scope.key) ? true : false);
            return <Scope key={key} scope={scope} checked={checked} onClick={this.check.bind(this)}/>
        });
    }

    /**
     * Check on/off ALL available scopes
     */
    globalCheckToggle() {
        if (getAvailableScopes(this.props.scopes).length === this.props.selectedScopes.length) {
            this.check([]);
        } else if (this.props.selectedScopes.length >= 0) {
            this.check(getAvailableScopes(this.props.scopes).map((scope) => scope.key));
        }
    }

    render() {
        const checkToggle = {
            icon: MousePointerClick,
            title: 'Select all',
            handler: this.globalCheckToggle.bind(this)
        };

        const buttons = [checkToggle];

        return (
            <>
                <ActionBar buttons={buttons} onSearch={this.filter.bind(this)} />
                <div className="ScopeSelector-scopes-container pt-4">
                    {this.renderScopesAndGroups(this.state.filterResults ? this.state.filterResults : this.props.scopes)}
                </div>
            </>
        );
    }
}


export const ScopeGroup = ({children, name}) => {
    return (
        <div className="ScopeGroup mb-4">
            <h2 className="text-sm font-medium text-foreground pl-1.5 mb-2">{name}</h2>
            {children}
        </div>
    )
}

export const Scope = ({scope, checked = false, onClick = () => {}}) => {
    const disabled = (!scope.available ? true : false);
    const onselect = (disabled ? () => {} : () => onClick(scope.key));
    const title = (disabled && scope.unavailableReason ? scope.unavailableReason : null);

    return (
        <div 
            className={`relative flex w-full cursor-pointer items-center gap-2 rounded-sm py-1.5 pr-8 pl-2 mb-[2px] text-sm outline-none select-none transition-colors ${
                disabled 
                    ? 'pointer-events-none opacity-50' 
                    : 'hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground'
            } ${checked ? 'bg-accent text-accent-foreground' : ''}`}
            onClick={onselect}
            title={title}
            data-disabled={disabled}
            data-selected={checked}
        >
            <div className="flex flex-col flex-1 min-w-0">
                <div className="text-sm text-foreground">{scope.name}</div>
                {scope.description && (
                    <div className="text-xs text-muted-foreground">{scope.description}</div>
                )}
            </div>
            {checked && (
                <span className="absolute right-2 flex size-3.5 items-center justify-center">
                    <Check className="size-4" />
                </span>
            )}
        </div>
    )
}


/* =======================

    Helpers

======================= */

export const filter = (rawTerm, source = []) => {
    if (!rawTerm || rawTerm === '') {
        return null;
    }

    // This lets us include special characters in the search term like + or -
    const term = rawTerm.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');

    const termIsNotIn = (value) => value.search(new RegExp(term, "i")) < 0;

    const filtered = source.filter((scope, key) => {
        if ((!scope.name || termIsNotIn(scope.name)) && (!scope.group || termIsNotIn(scope.group)) && (!scope.description || termIsNotIn(scope.description))) {
            return false;
        } else {
            return true;
        }
    });

    return filtered;
}

export const groupBy = (data, key = 'group') => {
    return data.reduce((groups, item) => {
        if (!item[key]) {
            const ungrouped = groups.ungrouped.concat(item);
            return Object.assign({}, groups, {ungrouped: ungrouped});
        }

        const groupKey = item[key];

        if (groups.items[groupKey]) {
            groups.items[groupKey].items = groups.items[groupKey].items.concat(item);
        } else {
            groups.items[groupKey] = {name: groupKey, items: [item]};
        }

        return groups;
    }, {items: {}, ungrouped: []});
};

export const getAvailableScopes = (scopes) => {
    return scopes.filter((scope) => scope.available);
}
